import { useEffect, useState } from 'react';

// Suspense fallback for every lazy()-loaded page. Shows a spinner
// immediately, and after CHUNK_LOAD_TIMEOUT_MS swaps to a recovery
// card. Without this, a chunk that 404s (deploy still in progress,
// CDN cache miss, network drop mid-fetch) would spin forever — same
// white-page-class bug we fixed for the auth flow.
//
// A RootErrorBoundary above the Suspense using this catches *thrown*
// ChunkLoadErrors, but a request that hangs (TCP stuck open) never
// throws — it just keeps the Suspense pending. That's the case this
// timeout covers.
//
// Extracted to its own file (was inline in App.tsx) so AppShell can use
// it too: the Suspense boundary around <Outlet/> needs the SAME
// fallback, scoped to the content column instead of the whole page —
// see AppShell.tsx for why that boundary exists separately.
const CHUNK_LOAD_TIMEOUT_MS = 12_000;

export default function PageSpinner() {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStuck(true), CHUNK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  if (stuck) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 460, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '28px 32px', boxShadow: '0 12px 32px rgba(15,23,42,0.08)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8 }}>Page taking too long</div>
          <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: 'var(--text-strong)' }}>This page isn't loading.</h2>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text)', lineHeight: 1.55 }}>
            A new version may have just deployed, or your connection is unstable. Reloading usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ background: 'var(--brand)', color: 'var(--brand-contrast)', border: 0, borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            Reload page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <div
        style={{
          width: 32,
          height: 32,
          border: '3px solid rgba(0,0,0,0.08)',
          borderTopColor: 'var(--pr)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
